import { ControlSymbol } from "../const";
// eslint-disable-next-line import/order
import { useCallback, useEffect, useRef, useState } from "react";

export type IntercomKeypadConfig = {
  maxDigits: number;
  onSubmit: (value: string) => Promise<void>;
  onChange: (value: string) => void;
  submitDelayMs: number;
};

export function useIntercomKeypad({
  maxDigits,
  onSubmit,
  onChange,
  submitDelayMs,
}: IntercomKeypadConfig) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [value, setValue] = useState<string>("");

  const changeValue = useCallback(
    (v: string) => {
      setValue(v);
      onChange(v);
    },
    [onChange],
  );

  const handleChange = async (symbol: string) => {
    switch (symbol) {
      case ControlSymbol.SUBMIT:
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        await onSubmit(value);
        changeValue("");
        break;

      case ControlSymbol.CLEAR:
        changeValue("");
        break;

      default:
        if (value.length < maxDigits) {
          changeValue(value + symbol);
        }
    }
  };

  useEffect(() => {
    timeoutRef.current = setTimeout(async () => {
      if (value.length > 0) {
        await onSubmit(value);
        changeValue("");
      }
    }, submitDelayMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [submitDelayMs, value, onSubmit, changeValue]);

  return {
    value,
    handleChange,
  };
}
